import { useState } from 'react'
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react'
import { DataTable, type Column } from './DataTable.js'
import { Button } from '../ui/Button.js'
import { CardHeader } from '../ui/Card.js'
import { Dialog } from '../ui/Dialog.js'
import { Field } from '../ui/Field.js'
import { Sheet } from '../ui/Sheet.js'
import { type MenuItem } from '../ui/DropdownMenu.js'
import { Stack } from '../layout/Stack.js'
import { toast } from '../feedback/Toaster.js'
import { whatWentWrong } from '../../lib/whatWentWrong.js'
import { StatusBanner } from '../feedback/StatusBanner.js'
import { StillNeeded, type Needed } from '../ui/StillNeeded.js'

export interface EditableField<T> {
  key: string
  header: React.ReactNode
  /** The control, given the row being edited and a way to change part of it. */
  edit: (draft: T, set: (patch: Partial<T>) => void) => React.ReactNode
  /** How it reads in the table. Falls back to nothing. */
  read?: (row: T) => React.ReactNode
  width?: string
  numeric?: boolean
  /**
   * A field that is shown but not asked for — a count, a status somebody
   * changes elsewhere. It keeps its column and stays out of the form.
   */
  readOnly?: boolean
  /** Under the control in the form. */
  hint?: React.ReactNode
}

export interface EditableListProps<T> {
  rows: T[] | undefined
  rowKey: (row: T) => string
  fields: EditableField<T>[]
  /** Called with the edited row. Throwing leaves the form open, still filled in. */
  onSave: (row: T) => Promise<unknown> | unknown
  /** A blank row. Omit where a list cannot be added to — the notification types. */
  onAdd?: () => T
  onRemove?: (row: T) => void
  /** Moves a row by one. Omit where order does not matter. */
  onMove?: (row: T, by: -1 | 1) => void
  /**
   * Anything this list offers that the generic four do not — a city's areas,
   * turning one off. They join Edit, Move and Remove in the same ⋯.
   */
  rowMenu?: (row: T) => MenuItem[]
  empty: React.ReactNode
  /** What this list is, when it has nothing in it. See `DataTable`. */
  emptyTitle?: string
  /** The query failed — see `DataTable`, which draws it instead of "nothing here". */
  listError?: boolean
  onRetry?: () => void
  loading?: boolean
  addLabel?: string
  /** Shown in the form when a save was refused. */
  error?: string | null
  /**
   * The section's heading. Given one, the list draws its own header with Add
   * on the right of it.
   *
   * Every section wrote `<CardHeader/>` then `<Card><EditableList/></Card>`
   * and let the Add button fall out of the bottom of the card — which put the
   * one control that starts something at the end of a table that may be
   * twenty rows long, a long way from the heading it belongs to. And the card
   * round a `DataTable` that already draws its own border was the *"double
   * border double layer"* the client called out.
   */
  title?: React.ReactNode
  description?: React.ReactNode
  /** What one row is called, for the form's title. "city", "role", "word". */
  noun?: string
  /** Forced, where the automatic choice is wrong. See `asSheet` below. */
  formAs?: 'dialog' | 'sheet'
  /**
   * What the form cannot be saved without, named.
   *
   * The lists here had no such idea, so an empty "A new category" went to
   * the server and came back as *"Some of that is not quite right."* — a
   * refusal about a form the form could have answered itself. Found walking
   * the desk on 2026-09-27. Same rule as every other form: Save is grey and
   * the line beside it says which.
   */
  missing?: (draft: T) => (string | Needed)[]
}

/**
 * A list you can add to and edit.
 *
 * Settings is seven of these — the cities and their areas, the categories, the
 * team, the roles, the mood words, the mindfulness activities, the notification
 * types — and every one is the same interaction over a different set of fields:
 * read a table, open a row, change it, save it, reorder it, turn it off.
 *
 * **Built before any of the seven exists.** That is the client's rule, and it was
 * worth following here against my own judgement: I argued this abstraction was
 * guesswork until the first section was written. That argument is exactly how
 * seven hand-rolled tables get built, each subtly different, before anybody
 * notices they were one thing.
 *
 * **A row is edited in a form, not in the row.** It used to swap each cell for
 * a control, so adding a city meant typing a name, a province, a time zone and
 * a sort order into four boxes squeezed into four table columns, with no
 * labels on any of them — the column heading two inches above was doing the
 * labelling. The client, on seeing it: *"why such bad row input design"*. The
 * form gives every field the label, the hint and the width it needs, and the
 * table goes back to being a table.
 *
 * **A dialog for a short form, a sheet for a long one.** Five fields or fewer
 * fit a centred box; more than that and a centred box grows off the top and
 * bottom of the window, taking its own Save button with it.
 *
 * **Explicitly, not as you type.** Always-live inputs on a settings table mean
 * a stray keystroke is a saved change to how the platform behaves, and the
 * person who made it never knew. Open, change, save — or cancel, and it is as
 * it was.
 *
 * It renders through `DataTable`, so the loading skeleton, the empty state, the
 * row menu and the keyboard behaviour are the ones every other table on the
 * desk has, rather than a second implementation of them.
 */
export function EditableList<T>({
  rows,
  rowKey,
  fields,
  onSave,
  onAdd,
  onRemove,
  onMove,
  rowMenu,
  empty,
  emptyTitle,
  listError,
  onRetry,
  loading,
  addLabel = 'Add',
  error,
  title,
  description,
  noun,
  formAs,
  missing,
}: EditableListProps<T>) {
  const [draft, setDraft] = useState<T | null>(null)
  /** What it looked like when the form opened, to tell a change from a glance. */
  const [opened, setOpened] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [saving, setSaving] = useState(false)
  /**
   * Why it did not close.
   *
   * The form deliberately stays open on a refusal, so somebody who got one
   * field wrong does not have to write the whole row again — and it said
   * nothing at all about why. A form that stays put after Save is pressed,
   * with no message, reads as a button that does not work.
   */
  const [refused, setRefused] = useState<string | null>(null)

  const asked = fields.filter((f) => !f.readOnly)
  /* Five is where a centred box stops fitting a 900px window once the head,
     the foot and the gaps are counted. */
  const asSheet = formAs ? formAs === 'sheet' : asked.length > 5

  const set = (patch: Partial<T>) => setDraft((d) => (d === null ? d : { ...d, ...patch }))
  const close = () => {
    setDraft(null)
    setOpened(null)
    setIsNew(false)
    setRefused(null)
  }

  /* Compared against what it was, not "has anybody typed" — typing a letter
     and deleting it again is not a change worth a question. */
  const dirty = draft !== null && opened !== null && JSON.stringify(draft) !== opened

  const needed = draft !== null && missing ? missing(draft) : []

  const save = async () => {
    if (draft === null || needed.length > 0) return
    setSaving(true)
    setRefused(null)
    try {
      await onSave(draft)
      /**
       * Say it landed.
       *
       * The seven settings lists saved in silence: the form closed and that
       * was the whole answer. For an add, the new row appearing is feedback;
       * for an **edit** it often is not — changing what a role may do, or a
       * city's time zone, changes something the table does not draw, so the
       * only difference on screen was a form that had gone.
       */
      toast.success(isNew ? `${sentenceCase(thing)} added.` : 'Saved.')
      close()
    } catch (e) {
      // Deliberately still open. Closing on failure would take the typing away
      // and put the old values back, so somebody who got one field wrong has to
      // write the whole row again to find out what it was. Now it says why.
      setRefused(whatWentWrong(e, `That ${thing} could not be saved.`))
    } finally {
      setSaving(false)
    }
  }

  const columns: Column<T>[] = fields.map((f): Column<T> => ({
    key: f.key,
    header: f.header,
    ...(f.width ? { width: f.width } : {}),
    ...(f.numeric ? { numeric: true } : {}),
    cell: (row) => f.read?.(row) ?? null,
  }))

  const menuFor = (row: T): MenuItem[] => [
    {
      label: 'Edit',
      icon: <Pencil />,
      onSelect: () => {
        setIsNew(false)
        setDraft(row)
        setOpened(JSON.stringify(row))
      },
    },
    ...(rowMenu ? rowMenu(row) : []),
    ...(onMove
      ? [
          { label: 'Move up', icon: <ArrowUp />, onSelect: () => onMove(row, -1) },
          { label: 'Move down', icon: <ArrowDown />, onSelect: () => onMove(row, 1) },
        ]
      : []),
    ...(onRemove
      ? [{ label: 'Remove', icon: <Trash2 />, destructive: true, onSelect: () => onRemove(row) }]
      : []),
  ]

  const add = onAdd && (
    <Button
      variant="secondary"
      size="sm"
      leadingIcon={<Plus aria-hidden />}
      onClick={() => {
        const blank = onAdd()
        setIsNew(true)
        setDraft(blank)
        setOpened(JSON.stringify(blank))
      }}
    >
      {addLabel}
    </Button>
  )

  const thing = noun ?? 'row'
  const sentenceCase = (w: string) => w.charAt(0).toUpperCase() + w.slice(1)
  const formTitle = isNew ? `A new ${thing}` : `Edit this ${thing}`

  const body = draft !== null && (
    <Stack gap={4}>
      {asked.map((f) => (
        <Field
          key={f.key}
          label={f.header}
          {...(f.hint ? { hint: f.hint } : {})}
          {...(error ? { error } : {})}
        >
          {() => f.edit(draft, set)}
        </Field>
      ))}
      {/* Under the fields, where the eye goes back to after the button. */}
      {refused && <StatusBanner tone="error">{refused}</StatusBanner>}
    </Stack>
  )

  const foot = (
    <>
      <StillNeeded fields={needed} />
      <Button variant="tertiary" disabled={saving} onClick={close}>
        Cancel
      </Button>
      <Button loading={saving} disabled={needed.length > 0} onClick={() => void save()}>
        Save
      </Button>
    </>
  )

  const form = asSheet ? (
    <Sheet
      open={draft !== null}
      onOpenChange={(open) => !open && close()}
      icon={<Pencil />}
      title={formTitle}
      dirty={dirty}
      onSubmit={() => void save()}
      footer={foot}
    >
      {body}
    </Sheet>
  ) : (
    <Dialog
      open={draft !== null}
      onOpenChange={(open) => !open && close()}
      icon={<Pencil />}
      title={formTitle}
      dirty={dirty}
      onSubmit={() => void save()}
      footer={foot}
    >
      {body}
    </Dialog>
  )

  const table = (
    <>
      <DataTable
        rows={rows}
        rowKey={rowKey}
        columns={columns}
        rowActions={menuFor}
        empty={empty}
        {...(emptyTitle ? { emptyTitle } : {})}
        {...(listError ? { error: true } : {})}
        {...(onRetry ? { onRetry } : {})}
        {...(loading ? { loading: true } : {})}
      />
      {form}
    </>
  )

  if (!title) {
    return (
      <div className="flex flex-col gap-3">
        {table}
        {add && <div>{add}</div>}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <CardHeader title={title} {...(description ? { description } : {})} actions={add} />
      {table}
    </div>
  )
}
